import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import { Card, CardContent, PageHeader, cn } from '@erp/ui';
import {
  Building2, Colorfilter, FileText, ClipboardList,
  ChevronRight, Zap, Truck, GridIcon, Banknote, SlidersHorizontal,
  ExternalLink, Database,
} from '@/components/icons';
import { usePermissions, type PermissionAction, type PermissionModule } from '@/hooks/use-permissions';

type SettingsItem = {
  titleKey: string;
  descriptionKey: string;
  path: string;
  icon: React.ReactNode;
  /** Hide the card when the current user lacks this permission. */
  permission?: { module: PermissionModule | string; action: PermissionAction };
};

type SettingsSection = {
  titleKey: string;
  descriptionKey: string;
  items: SettingsItem[];
};

const ICON_SIZE = 20;

const SECTIONS: SettingsSection[] = [
  {
    titleKey: 'settingsHub.sections.business.title',
    descriptionKey: 'settingsHub.sections.business.description',
    items: [
      {
        titleKey: 'nav.settingsCompany',
        descriptionKey: 'settingsHub.items.company',
        path: '/settings/company',
        icon: <Building2 size={ICON_SIZE} />,
      },
      {
        titleKey: 'nav.settingsBranding',
        descriptionKey: 'settingsHub.items.branding',
        path: '/settings/branding',
        icon: <Colorfilter size={ICON_SIZE} />,
      },
      {
        titleKey: 'nav.settingsAppBuilder',
        descriptionKey: 'settingsHub.items.appBuilder',
        path: '/settings/app-builder',
        icon: <SlidersHorizontal size={ICON_SIZE} />,
      },
      {
        titleKey: 'nav.settingsNavigation',
        descriptionKey: 'settingsHub.items.navigation',
        path: '/settings/navigation',
        icon: <ExternalLink size={ICON_SIZE} />,
      },
      {
        titleKey: 'nav.settingsQuickLinks',
        descriptionKey: 'settingsHub.items.quickLinks',
        path: '/settings/quick-links',
        icon: <Zap size={ICON_SIZE} />,
      },
      {
        titleKey: 'nav.settingsModules',
        descriptionKey: 'settingsHub.items.modules',
        path: '/settings/modules',
        icon: <GridIcon size={ICON_SIZE} />,
      },
      {
        titleKey: 'settingsHub.items.billingTitle',
        descriptionKey: 'settingsHub.items.billing',
        path: '/settings/billing',
        icon: <Banknote size={ICON_SIZE} />,
      },
    ],
  },
  {
    titleKey: 'settingsHub.sections.team.title',
    descriptionKey: 'settingsHub.sections.team.description',
    items: [
      {
        titleKey: 'settingsHub.items.teamTitle',
        descriptionKey: 'settingsHub.items.team',
        path: '/settings/team',
        icon: <Users size={ICON_SIZE} />,
        permission: { module: 'team', action: 'view' },
      },
    ],
  },
  {
    titleKey: 'settingsHub.sections.system.title',
    descriptionKey: 'settingsHub.sections.system.description',
    items: [
      {
        titleKey: 'nav.settingsDatabaseImport',
        descriptionKey: 'settingsHub.items.databaseImport',
        path: '/settings/database-import',
        icon: <Database size={ICON_SIZE} />,
      },
    ],
  },
  {
    titleKey: 'settingsHub.sections.documents.title',
    descriptionKey: 'settingsHub.sections.documents.description',
    items: [
      {
        titleKey: 'nav.settingsPdfTemplates',
        descriptionKey: 'settingsHub.items.pdfTemplates',
        path: '/settings/pdf-templates',
        icon: <FileText size={ICON_SIZE} />,
      },
      {
        titleKey: 'nav.settingsArticleTickets',
        descriptionKey: 'settingsHub.items.articleTickets',
        path: '/settings/article-tickets',
        icon: <ClipboardList size={ICON_SIZE} />,
      },
    ],
  },
  {
    titleKey: 'settingsHub.sections.delivery.title',
    descriptionKey: 'settingsHub.sections.delivery.description',
    items: [
      {
        titleKey: 'nav.settingsDeliveryProviders',
        descriptionKey: 'settingsHub.items.deliveryProviders',
        path: '/settings/delivery-providers',
        icon: <Truck size={ICON_SIZE} />,
      },
    ],
  },
];

export function SettingsHubPage() {
  const { t } = useTranslation();
  const { can, isLoading: permsLoading } = usePermissions();

  // Filter cards by permission. Permissive while loading so the hub
  // renders quickly; the next render trims it once perms arrive.
  const visibleSections = SECTIONS
    .map((section) => ({
      ...section,
      items: section.items.filter((it) => {
        if (!it.permission || permsLoading) return true;
        return can(it.permission.module, it.permission.action);
      }),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('settingsHub.title')}
        subtitle={t('settingsHub.subtitle')}
      />

      <div className="space-y-8">
        {visibleSections.map((section) => (
          <section key={section.titleKey} className="space-y-3">
            <div className="flex items-baseline gap-2">
              <h2 className="text-sm font-semibold tracking-tight text-foreground">
                {t(section.titleKey)}
              </h2>
              <p className="text-sm text-muted-foreground truncate">
                {t(section.descriptionKey)}
              </p>
            </div>

            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {section.items.map((item) => (
                <SettingsCard key={item.path} item={item} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function SettingsCard({ item }: { item: SettingsItem }) {
  const { t } = useTranslation();

  return (
    <Link
      to={item.path}
      className={cn(
        'group block rounded-lg outline-none',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      )}
    >
      <Card
        className={cn(
          'h-full transition-all duration-150',
          'hover:border-primary/40 hover:shadow-sm group-hover:-translate-y-0.5',
        )}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'flex size-10 shrink-0 items-center justify-center rounded-md',
                // Use neutral tint + foreground text so the icon is
                // readable regardless of the user's chosen primary color
                // (a near-black brand colour rendered as `text-primary`
                // becomes invisible against the dark mode background).
                'bg-foreground/[0.06] text-foreground',
                'group-hover:bg-primary group-hover:text-primary-foreground transition-colors',
              )}
            >
              {item.icon}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-medium text-foreground truncate">
                  {t(item.titleKey)}
                </h3>
                <ChevronRight
                  size={16}
                  className="shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100"
                />
              </div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground line-clamp-2">
                {t(item.descriptionKey)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
